import type { QueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateSolarFinancingLockReaders } from "./solar-financing-lock-readers"
import type { SolarSungageApplicationStateDto } from "../dto/solar-sungage.dto"

/**
 * An approval adopts its terms into the Quote (the investment section and
 * Share read it) and locks the selections, which every read behind the
 * financing lock reports. Not awaited.
 */
const invalidateApplicationReaders = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  for (const queryKey of [
    queryKeys.solarProposal.pricing(proposalId),
    queryKeys.solarProposal.share(proposalId),
  ]) {
    void queryClient.invalidateQueries({ queryKey, exact: true })
  }
  invalidateSolarFinancingLockReaders(queryClient, proposalId)
}

/**
 * What, once it moves, the Quote, the lock, contract readiness and the share
 * follow: the attempt, its credit status, terms, review flag and expiration,
 * and what its loan status follows besides the credit status (the project's
 * signing and funding). The read carries no loan status of its own.
 */
const decision = (state: SolarSungageApplicationStateDto | undefined): string =>
  JSON.stringify({
    applicationId: state?.application?.applicationId ?? null,
    creditStatus: state?.application?.creditStatus ?? null,
    termsKind: state?.application?.termsKind ?? null,
    approvedTerms: state?.application?.approvedTerms ?? null,
    reviewNeeded: state?.application?.reviewNeeded ?? null,
    financePlanMismatch: state?.application?.financePlanMismatch ?? null,
    expiration: state?.application?.expiration ?? null,
    webhookEventName:
      state?.application?.projectProgress.webhookEventName ?? null,
    dateFunded: state?.application?.projectProgress.dateFunded ?? null,
  })

/**
 * Every Sungage command answers with the read it just wrote. The readers
 * behind the Quote and the lock refetch only when the decision moved, so the
 * drawer's 45-second polling doesn't refetch the proposal each time.
 */
export const applySolarSungageApplicationState = (
  queryClient: QueryClient,
  proposalId: string,
  state: SolarSungageApplicationStateDto,
): void => {
  const queryKey = queryKeys.solarProposal.sungageApplication(proposalId)
  const previous =
    queryClient.getQueryData<SolarSungageApplicationStateDto>(queryKey)
  queryClient.setQueryData(queryKey, {
    application: state.application,
    start: state.start,
    lastStartFailure: state.lastStartFailure,
  } satisfies SolarSungageApplicationStateDto)
  if (previous == null || decision(previous) !== decision(state)) {
    invalidateApplicationReaders(queryClient, proposalId)
  }
}

/**
 * A failed command, or a gateway notification (a Sungage webhook the gateway
 * reconciled), may still have moved the application: read it all again.
 */
export const invalidateSolarSungageDependents = (
  queryClient: QueryClient,
  proposalId: string,
): void => {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.solarProposal.sungageApplication(proposalId),
    exact: true,
  })
  invalidateApplicationReaders(queryClient, proposalId)
}
